import type { ToolId } from './partners';

/**
 * Core game model.
 *
 * The central idea: a scenario contains a *faithful* description of a tool's
 * state (issues, columns, sprint) and, separately, a list of the defects that
 * were planted in it. The renderer never reads the defect list -- it just draws
 * whatever the data says. Grading never reads the board -- it just compares
 * flagged targets against the defect list.
 *
 * That separation is what lets a new scenario be pure data, and it means a
 * defect cannot be "visible" to the renderer as a special case. If a planted
 * defect is not apparent from the rendered board, that is a content bug.
 */

export type Severity = 'critical' | 'major' | 'minor';

export type IssueType = 'epic' | 'story' | 'bug' | 'task' | 'subtask';

export type Priority = 'highest' | 'high' | 'medium' | 'low' | 'lowest';

export interface Person {
  id: string;
  name: string;
  initials: string;
  /** Avatar background colour. */
  color: string;
  /** A deactivated account. Still renderable, still assignable -- that's the point. */
  inactive?: boolean;
}

export interface Issue {
  /** Human-facing ticket key, e.g. "ENG-412". Also the target id. */
  key: string;
  type: IssueType;
  summary: string;
  assigneeId?: string;
  /** Story points. Absent means unestimated. */
  points?: number;
  /** Hour estimate. Present on a points board is itself a smell. */
  estimateHours?: number;
  priority: Priority;
  labels?: string[];
  /** Keys of issues blocking this one. */
  blockedBy?: string[];
  parentKey?: string;
  /** Which sprint the issue is assigned to, if any. */
  sprint?: string;
  startDate?: string;
  dueDate?: string;
  /** Days since last status change. Drives the staleness indicator. */
  idleDays?: number;
  /** Linked merge/pull request, if any. */
  linkedPr?: string;
  /** Number of acceptance criteria recorded on the issue. */
  acceptanceCriteria?: number;
}

export interface ColumnDef {
  id: string;
  name: string;
  /** Work-in-progress limit, if the column has one configured. */
  wipLimit?: number;
  issueKeys: string[];
  /** Marks the terminal column, which changes how some checks read. */
  isDone?: boolean;
}

export interface SprintDef {
  name: string;
  goal: string;
  /** Team's established velocity in points -- the yardstick for commitment. */
  velocity: number;
  startDate: string;
  endDate: string;
  daysRemaining: number;
}

/** The three things on the board a player can flag. */
export type TargetKind = 'issue' | 'column' | 'sprint';

export interface Target {
  kind: TargetKind;
  id: string;
}

export interface Defect {
  id: string;
  target: Target;
  /**
   * Other targets that count as spotting the same defect. Needed whenever a
   * defect is a relationship rather than a property -- a duplicate pair, or a
   * blocked/blocker pair -- where the player could reasonably point at either
   * end. Any of these credits the defect, and none of them count as a false
   * positive.
   */
  altTargets?: Target[];
  /** Short name shown in the results list. */
  title: string;
  severity: Severity;
  /** Why this is wrong -- the actual teaching payload. */
  explanation: string;
  /** What a competent team would do about it. */
  fix: string;
}

/**
 * What a scenario depicts, and who owns the thing depicted. Mirrors the image
 * scenarios' field so both kinds of level declare their source the same way.
 */
export interface ScenarioSource {
  /** See the image scenarios' ScenarioSource: names live in `partners.ts`. */
  toolId: ToolId;
  screen: string;
  kind: 'screenshot' | 'recreation';
  note?: string;
}

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  project: string;
  /** What this level depicts and who owns it. Required on every scenario. */
  source: ScenarioSource;
  people: Person[];
  issues: Issue[];
  columns: ColumnDef[];
  sprint: SprintDef;
  defects: Defect[];
}

/** Stable string key for a target, used as the identity in flag sets. */
export function targetKey(target: Target): string {
  return `${target.kind}:${target.id}`;
}

export function makeKey(kind: TargetKind, id: string): string {
  return `${kind}:${id}`;
}
